import axios from "axios";
import { appSettings } from "@/appSettings";

// Single seam that absorbs the backend contract for the Organisation-typed
// registration form (FOYS-23856 / companion backend ticket FOYS-23855, and
// FOYS-23857 which adds the fieldsConfiguration-driven question sections).
//
// GET/POST/error conventions match the Person endpoint exactly (400
// ProblemDetails for all rejections, 404 for missing/wrong-type form).
// Components consume the normalised shape below, not raw field names, so if
// the backend ever renames something this file is the only place that needs
// to change.
//
// FOYS-23857 contract, confirmed with the backend dev:
// - `fieldsConfiguration` arrives in the same shape as Person's (array of
//   { name, description, isCustom, fields: [...] }), plus `tenantId`/
//   `defaultCountryCode` at the top level (already present on the org GET
//   response today). `allowDirectPayment` and `hidePrices` are Person-form
//   settings; organisation forms have no price hiding and use `paymentMode`
//   (FOYS-27334) for payment.
// - Module names: GeneralInfo (Name, ContactEmail - non-custom, generic),
//   ContactInfo (Email.email, Phone.number, Link.address - non-custom,
//   generic, NOT a reuse of Person's Address/AddressInfo.* shape since
//   organisations have no postal address entity), FinancialDetails (reuses
//   Person's BillingInfoDto.* shape as-is), OrganisationPlanAssignment
//   (PlanAssignment.organisationPlanId as a Dropdown + PlanAssignment.
//   startDate - non-custom, generic; deliberately NOT MembershipDetails,
//   since orgs have no age/birthdate concept), Files (isCustom, reused
//   as-is). Custom fields use the same CustomFieldValues[n] mechanism as
//   Person, just sourced from CustomFieldDestination.Organisation.
// - POST payload is additive: Name/ContactEmail/RecaptchaToken/
//   ValidationToken stay as today, plus Email/Phone/Link/BillingInfoDto/
//   PlanAssignment/CustomFieldValues/Files using the same dotted-key-path
//   convention as Person throughout.

// IMPORTANT: the error body (ProblemDetails) is serialized with PascalCase
// keys (Detail/Status/Title/ErrorCode), NOT the camelCase used by the
// success-response DTOs below (id/name/title/etc. stay camelCase - only the
// error-object serialization path differs). Confirmed empirically by the
// backend dev against this API's actual JsonSerializerSettings. Don't
// "fix" these to lowercase.
const DUPLICATE_ORGANISATION_NAME_ERROR_CODE = "duplicate_organisation_name";

// FOYS-27334: how the organisation form handles payment.
export const ORGANISATION_PAYMENT_MODES = {
	sendManually: "SendManually",
	directPayment: "DirectPayment",
	payNowOrLater: "PayNowOrLater",
};

/**
 * FOYS-27334: whether the organisation form takes payment (DirectPayment /
 * PayNowOrLater). SendManually forms keep the pre-existing behaviour.
 */
export function isOrganisationPaymentMode(formConfig) {
	return (
		!!formConfig &&
		!!formConfig.paymentMode &&
		formConfig.paymentMode !== ORGANISATION_PAYMENT_MODES.sendManually
	);
}

export const ORGANISATION_PAYMENT_CHOICES = {
	payNow: "PayNow",
	payLater: "PayLater",
};

function getBaseUrl(configId) {
	return `${appSettings.FoysPubRegistrationsUrl}/organisation-registration-forms/${configId}`;
}

/**
 * Fetches the public config for an Organisation-typed registration form and
 * normalises it to the shape consumed by OrganisationRegistrationForm.vue.
 */
export async function getOrganisationRegistrationForm(configId) {
	const response = await axios.get(getBaseUrl(configId));
	const data = response.data;

	const fieldsConfiguration = data.fieldsConfiguration ?? [];

	// Mirror RegistrationForm.vue's post-processing: every field in a
	// non-custom module starts out visible. Individual modules
	// (FinancialDetailsModule etc.) toggle this off/on afterwards.
	for (const formModule of fieldsConfiguration.filter((x) => !x.isCustom)) {
		(formModule.fields ?? []).forEach((field) => {
			field.isVisible = true;
		});
	}

	return {
		id: data.id ?? configId,
		title: data.title,
		description: data.description,
		locale: data.locale,
		privacyPolicyUrl: data.privacyPolicyUrl,
		termsAndConditionsUrl: data.termsAndConditionsUrl,
		termsAndConditionsText: data.termsAndConditionsText,
		thankYouMessage: data.thankYouMessage,
		successRedirectUrl: data.successRedirectUrl,
		recaptchaSiteKey: data.recaptchaSiteKey,
		// Despite the Person-era field name, the backend confirmed this is now
		// the generic admin-authored "a duplicate was found" message, reused
		// as-is (same DTO property/JSON key) for organisation forms.
		duplicateExistsMessage: data.textToShowWhenPersonExists ?? null,
		// FOYS-23857: question-section rendering. See file header for the
		// confirmed module/field contract.
		fieldsConfiguration,
		tenantId: data.tenantId ?? null,
		defaultCountryCode: data.defaultCountryCode ?? null,
		allowDirectPayment: !!data.allowDirectPayment,
		allowMultiplePlans: !!data.allowMultiplePlans,
		currencyCode: data.currencyCode ?? null,
		// FOYS-27334: SendManually keeps the pre-existing behaviour (no amount
		// calculation, no payment step).
		paymentMode: data.paymentMode || ORGANISATION_PAYMENT_MODES.sendManually,
	};
}

/**
 * The multi-plan picker stores a number[] under the scalar's key; the API
 * expects it as organisationPlanIds. Shared by submit and the amount call.
 */
function normalisePlanAssignment(planAssignment) {
	if (planAssignment && Array.isArray(planAssignment.organisationPlanId)) {
		const { organisationPlanId, ...rest } = planAssignment;
		return {
			...rest,
			organisationPlanIds: [...organisationPlanId],
		};
	}

	return planAssignment;
}

/**
 * FOYS-27334: whether at least one plan is selected (single or multiple plan
 * picker). The amount endpoint is only called once one is.
 */
export function hasSelectedOrganisationPlan(planAssignment) {
	if (!planAssignment) {
		return false;
	}

	const value = planAssignment.organisationPlanId;

	return Array.isArray(value)
		? value.length > 0
		: value !== null && value !== undefined && value !== "";
}

/**
 * FOYS-27334: calculates the amount for the current plan selection and
 * billing info (DirectPayment / PayNowOrLater forms only; no VAT). Any error
 * means the amount could not be loaded; callers show a retry and still allow
 * submit.
 *
 * Lines carry `organisationPlanId`, `kind`, `name`, `quantity`, `unitPrice`
 * and `amount` (unitPrice x quantity); `plans` holds one amount per selected
 * plan, in selection order. Nothing billable means no lines and no plans.
 *
 * @param {string} configId
 * @param {object} input the form's field bag (PlanAssignment, BillingInfoDto)
 * @returns {Promise<{ lines: object[], plans: object[], totalAmount: number,
 *   currencyCode: string, paymentRequired: boolean }>}
 */
export async function calculateOrganisationAmount(configId, input) {
	let billingInfoDto = input.BillingInfoDto ?? null;

	// The IBAN only matters with debt collection on (same as v2).
	if (billingInfoDto && !billingInfoDto.debtCollection) {
		// eslint-disable-next-line no-unused-vars
		const { iban, ...rest } = billingInfoDto;
		billingInfoDto = rest;
	}

	const response = await axios.post(`${getBaseUrl(configId)}/amount`, {
		planAssignment: normalisePlanAssignment(input.PlanAssignment) ?? null,
		billingInfoDto,
	});
	const data = response.data;

	return {
		lines: (data.lines ?? []).map((line) => ({
			organisationPlanId: line.organisationPlanId ?? null,
			kind: line.kind ?? null,
			name: line.name ?? "",
			quantity: line.quantity ?? 1,
			unitPrice: line.unitPrice ?? 0,
			amount: line.amount ?? 0,
		})),
		plans: (data.plans ?? []).map((plan) => ({
			organisationPlanId: plan.organisationPlanId,
			amount: plan.amount ?? 0,
		})),
		totalAmount: data.totalAmount ?? 0,
		currencyCode: data.currencyCode ?? null,
		paymentRequired: !!data.paymentRequired,
	};
}

// Plan ids may arrive as number or string (dropdown values); null = no plan.
function isSamePlanId(a, b) {
	if (a === null || a === undefined || b === null || b === undefined) {
		return (a ?? null) === (b ?? null);
	}

	return String(a) === String(b);
}

/**
 * FOYS-27334: the lines of one selected plan.
 */
export function getOrganisationAmountLines(amountResult, organisationPlanId) {
	return ((amountResult && amountResult.lines) || []).filter(
		(line) => isSamePlanId(line.organisationPlanId, organisationPlanId)
	);
}

/**
 * Submits the Organisation registration.
 *
 * FOYS-23857: `input` is now the full dynamic field bag assembled by
 * OrganisationRegistrationForm.vue from the fieldsConfiguration-driven
 * sections (Name/ContactEmail from GeneralInfo, Email.email/Phone.number/
 * Link.address from ContactInfo, BillingInfoDto.* from FinancialDetails,
 * PlanAssignment.organisationPlanId/startDate from OrganisationPlanAssignment,
 * CustomFieldValues[n], Files) - the same dotted-key-path convention
 * RegistrationForm/ConfirmationForm use for the Person POST - plus
 * `recaptchaToken`/`validationToken`. Per the contract confirmed with the
 * backend dev this is additive to the pre-existing Name/ContactEmail/
 * RecaptchaToken/ValidationToken properties, not a replacement. Applies the
 * same request cleanup ConfirmationForm.vue does for Person (dropping empty
 * CustomFieldValues slots, stripping confirmation-only helper fields).
 *
 * FOYS-27334: `input.paymentChoice` ("PayNow" | "PayLater") is passed through
 * for PayNowOrLater forms. A non-null `paymentLink` in the result means the
 * browser must be redirected to it.
 *
 * @param {string} configId
 * @param {object} input
 * @returns {Promise<{ organisationId: string, paymentLink: string|null }>}
 */
export async function submitOrganisationRegistration(configId, input) {
	const requestData = { ...input };

	// Omit/empty means "no confirmation email" per the confirmed contract.
	requestData.ContactEmail = input.ContactEmail || undefined;

	if (requestData.CustomFieldValues) {
		requestData.CustomFieldValues = requestData.CustomFieldValues.filter((x) => x);
	}

	requestData.PlanAssignment = normalisePlanAssignment(requestData.PlanAssignment);

	if (!requestData.paymentChoice) {
		delete requestData.paymentChoice;
	}

	delete requestData.ConfirmPassword;
	delete requestData.ConfirmEmail;

	const response = await axios.post(getBaseUrl(configId), requestData);

	return {
		organisationId: response.data.organisationId,
		paymentLink: response.data.paymentLink || null,
	};
}

/**
 * Classifies "duplicate organisation name" API errors so the component can
 * show the duplicate-detection modal (using the GET response's
 * duplicateExistsMessage) instead of the generic error banner.
 *
 * Per the confirmed contract this is a 400, not a 409 - every rejection
 * (missing name, duplicate name, bad validation/recaptcha token) comes back
 * as 400 ProblemDetails. The duplicate-name case is the only one that also
 * carries an `ErrorCode` property (absent on every other rejection), so
 * check for its presence/value rather than assuming it's always there.
 */
export function isDuplicateOrganisationError(error) {
	return !!(
		error &&
		error.response &&
		error.response.status === 400 &&
		error.response.data &&
		error.response.data.ErrorCode === DUPLICATE_ORGANISATION_NAME_ERROR_CODE
	);
}

/**
 * Extracts the developer-facing error message from a ProblemDetails
 * response, falling back to a generic message key if the shape doesn't
 * match (network error, unexpected 5xx, etc). Note: PascalCase `Detail`,
 * per this API's actual JSON serialization of the error object.
 */
export function getErrorDetail(error) {
	return error && error.response && error.response.data
		? error.response.data.Detail
		: null;
}
